import { useCallback, useState } from 'react';
import { opponent as otherPlayer } from '@tentactoe/engine';
import type { Player } from '@tentactoe/engine';
import { Board } from './components/Board';
import { GameSetup, HUMAN } from './components/GameSetup';
import { Status } from './components/Status';
import { useBotOpponent } from './bot/useBotOpponent';
import { useGame } from './game/useGame';
import { defaultTheme } from './theme';

export default function App() {
  const { state, play, undo, reset, canUndo } = useGame();
  const [opponentId, setOpponentId] = useState<string>(HUMAN);
  const [side, setSide] = useState<Player>('X');

  const theme = defaultTheme;
  const { classes } = theme;

  const versusBot = opponentId !== HUMAN;
  const botSide = versusBot ? otherPlayer(side) : null;

  const { thinking, error } = useBotOpponent(state, play, {
    enabled: versusBot,
    botId: opponentId,
    side: botSide ?? 'O',
  });

  const botToMove = botSide !== null && state.result === null && state.turn === botSide;

  // Changing the matchup mid-game would leave a half-played position with the
  // wrong sides, so both controls restart instead.
  const changeOpponent = useCallback(
    (next: string) => {
      setOpponentId(next);
      reset();
    },
    [reset],
  );

  const changeSide = useCallback(
    (next: Player) => {
      setSide(next);
      reset();
    },
    [reset],
  );

  /** Undo the bot's reply as well, so one click returns control to the player. */
  const undoTurn = useCallback(() => {
    undo();
    if (versusBot && state.moves.length >= 2) undo();
  }, [undo, versusBot, state.moves.length]);

  return (
    <div className={classes.page}>
      <h1 className={classes.title}>Ultimate Tic-Tac-Toe</h1>

      <GameSetup
        opponent={opponentId}
        onOpponentChange={changeOpponent}
        side={side}
        onSideChange={changeSide}
        theme={theme}
        disabled={state.moves.length > 0}
      />

      <Status state={state} theme={theme} />

      <div className="h-6 mb-1" aria-live="polite">
        {thinking && <span className={classes.thinking}>Thinking…</span>}
        {error && <span className="text-sm text-red-600">Bot error: {error}</span>}
      </div>

      <Board state={state} theme={theme} onPlay={play} locked={botToMove} />

      <div className="mt-6 flex gap-3">
        <button
          type="button"
          onClick={undoTurn}
          disabled={!canUndo || botToMove}
          className={canUndo && !botToMove ? classes.button : classes.buttonDisabled}
        >
          Undo
        </button>
        <button type="button" onClick={reset} className={classes.button}>
          New game
        </button>
      </div>

      <div className="mt-8 text-gray-600 text-center max-w-2xl">
        <h2 className="text-lg font-semibold mb-2">How to play</h2>
        <ul className="text-sm space-y-1">
          <li>X goes first and can play anywhere.</li>
          <li>The cell you play decides which mini-board your opponent must play in.</li>
          <li>If you are sent to a board that is already won or full, you may play anywhere.</li>
          <li>Win a mini-board with three in a row; win the game by winning three in a row.</li>
        </ul>
      </div>
    </div>
  );
}
